import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { Tier } from '../api/types.ts';
import { Icon } from '../components/Icon.tsx';
import { tierName } from '../components/UpgradePrompt.tsx';
import { t } from '../i18n/index.ts';
import { useDocumentTitle } from '../lib/documentTitle.ts';
import { useStore } from '../state/store.tsx';

const POLL_MS = 1500;
/** About 30 seconds: Stripe's webhook normally lands well within that. */
const POLL_ATTEMPTS = 20;

type Confirmation = { status: 'checking' } | { status: 'done'; tier: Tier } | { status: 'slow' };

/** Back from Stripe Checkout: waits for the webhook to show up in GET /api/billing. */
export function BillingSuccessScreen() {
  useDocumentTitle(t('docTitle.billingSuccess'));
  const { refreshBilling, refreshPlans } = useStore();
  const [state, setState] = useState<Confirmation>({ status: 'checking' });
  const [round, setRound] = useState(0);

  useEffect(() => {
    let live = true;
    let timer = 0;
    let attempts = 0;
    const check = async () => {
      attempts += 1;
      const billing = await refreshBilling();
      if (!live) return;
      if (billing && billing.tier !== 'none') {
        setState({ status: 'done', tier: billing.tier });
        void refreshPlans();
        return;
      }
      if (attempts >= POLL_ATTEMPTS) setState({ status: 'slow' });
      else timer = window.setTimeout(() => void check(), POLL_MS);
    };
    setState({ status: 'checking' });
    void check();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [refreshBilling, refreshPlans, round]);

  if (state.status === 'done') {
    return (
      <div className="state-panel billing-return">
        <span className="state-panel__icon billing-return__icon--done" aria-hidden="true"><Icon name="checkCircle" size={28} /></span>
        <h1 className="state-panel__title">{t('billingReturn.success.title', { tier: tierName(state.tier) })}</h1>
        <p className="state-panel__body">{t('billingReturn.success.body')}</p>
        <div className="button-row billing-return__actions">
          <Link to="/" className="button button--primary">{t('billingReturn.success.home')}</Link>
          <Link to="/settings#billing" className="button button--outline">{t('billingReturn.success.billing')}</Link>
        </div>
      </div>
    );
  }
  const slow = state.status === 'slow';
  return (
    <div className="state-panel billing-return" aria-busy={!slow}>
      <span className="state-panel__icon billing-return__icon" aria-hidden="true">
        {slow ? <Icon name="card" size={26} /> : <span className="billing-return__spinner" />}
      </span>
      <div className="billing-return__text" role="status">
        <h1 className="state-panel__title">{slow ? t('billingReturn.slow.title') : t('billingReturn.confirming.title')}</h1>
        <p className="state-panel__body">{slow ? t('billingReturn.slow.body') : t('billingReturn.confirming.body')}</p>
      </div>
      {slow && (
        <div className="button-row billing-return__actions">
          <button type="button" className="button button--primary" onClick={() => setRound((r) => r + 1)}>{t('billingReturn.slow.retry')}</button>
          <Link to="/" className="button button--outline">{t('billingReturn.success.home')}</Link>
        </div>
      )}
    </div>
  );
}

export function BillingCancelledScreen() {
  useDocumentTitle(t('docTitle.billingCancelled'));
  return (
    <div className="state-panel billing-return">
      <span className="state-panel__icon billing-return__icon" aria-hidden="true"><Icon name="card" size={26} /></span>
      <h1 className="state-panel__title">{t('billingReturn.cancelled.title')}</h1>
      <p className="state-panel__body">{t('billingReturn.cancelled.body')}</p>
      <div className="button-row billing-return__actions">
        <Link to="/upgrade" className="button button--primary">{t('billingReturn.cancelled.back')}</Link>
        <Link to="/" className="button button--outline">{t('billingReturn.success.home')}</Link>
      </div>
    </div>
  );
}
